"use client";

import { useEffect } from "react";
import Icon from "@/components/ui/AppIcon";

interface TimelineStep {
  id: string;
  number: string;
  title: string;
  description: string;
  duration: string;
  icon: string;
}

export default function ProcessTimeline() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate");
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -5% 0px" }
    );

    document.querySelectorAll(".animate-on-scroll").forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const steps: TimelineStep[] = [
    {
      id: "step_1",
      number: "01",
      title: "Discovery",
      description:
        "Initial consultation to understand your vision, goals, and requirements. We discuss budget, timeline, and creative direction.",
      duration: "1-2 weeks",
      icon: "ChatBubbleLeftRightIcon",
    },
    {
      id: "step_2",
      number: "02",
      title: "Planning",
      description:
        "Detailed pre-production planning including scripting, storyboarding, location scouting, and crew assignment.",
      duration: "2-4 weeks",
      icon: "DocumentTextIcon",
    },
    {
      id: "step_3",
      number: "03",
      title: "Production",
      description:
        "On-set filming or live event execution with full crew, equipment, and technical support. Real-time coordination and quality control.",
      duration: "1-6 weeks",
      icon: "VideoCameraIcon",
    },
    {
      id: "step_4",
      number: "04",
      title: "Post-Production",
      description:
        "Professional editing, color grading, sound design, and visual effects. Multiple review rounds to ensure perfection.",
      duration: "2-8 weeks",
      icon: "PencilSquareIcon",
    },
    {
      id: "step_5",
      number: "05",
      title: "Delivery",
      description:
        "Final mastering and delivery in your required formats. Ongoing support and archival services available.",
      duration: "1 week",
      icon: "RocketLaunchIcon",
    },
  ];

  return (
    <section className="py-24 md:py-32 px-6 md:px-12 bg-[#080808] border-t border-white/5 relative">
      {/* Decorative Section Number */}
      <div className="absolute top-12 right-6 md:right-12 z-0 opacity-[0.05] font-display font-bold text-[8rem] md:text-[10rem] leading-none text-white pointer-events-none select-none">
        PROCESS
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-16 animate-on-scroll [animation:animationIn_0.8s_ease-out_0.2s_both]">
          <h2 className="text-3xl md:text-4xl font-display font-light text-white mb-4">
            Our Production Process
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto font-light">
            A structured approach from concept to delivery, ensuring quality at every stage.
          </p>
        </div>

        <div className="relative">
          {/* Connection Line */}
          <div className="hidden md:block absolute top-16 left-0 right-0 h-0.5 bg-gradient-to-r from-primary via-secondary to-accent opacity-30"></div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
            {steps.map((step, index) => (
              <div
                key={step.id}
                className={`relative animate-on-scroll [animation:animationIn_0.8s_ease-out_${
                  0.3 + index * 0.1
                }s_both]`}
              >
                {/* Step Number Circle */}
                <div className="flex justify-center mb-6">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white font-display font-bold text-xl shadow-lg relative z-10">
                    {step.number}
                  </div>
                </div>

                {/* Content Card */}
                <div className="glass-card p-6 rounded-2xl text-center">
                  <div className="w-12 h-12 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4 text-primary">
                    <Icon name={step.icon as any} size={24} />
                  </div>
                  <h3 className="text-lg font-display font-medium text-white mb-3">
                    {step.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                    {step.description}
                  </p>
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/30">
                    <Icon name="ClockIcon" size={14} className="text-primary" />
                    <span className="text-xs font-medium text-primary">
                      {step.duration}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}